import React, { useState } from 'react';
import { Form, Button, Message, Checkbox } from 'semantic-ui-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RecipeSaveForm.css';
import { recipes } from '../../services/api';

function RecipeSaveForm({ result, recipeOils }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    notes: '',
    isPublic: true
  });
  const [error, setError] = useState('');

  const { mutate: saveRecipe, isLoading } = useMutation({
    mutationFn: (data) => recipes.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['recipes']);
      setFormData({ name: '', description: '', notes: '', isPublic: true });
      setError('');
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Failed to save recipe');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Recipe name is required');
      return;
    }

    if (!result) {
      setError('Recipe calculation result is required');
      return;
    }

    const recipeData = {
      name: formData.name,
      description: formData.description,
      notes: formData.notes,
      visibility: formData.isPublic ? 1 : 0,
      oils: recipeOils.map(oil => ({
        id: oil.id,
        weight: Number(oil.percentage) || Number(oil.amount) || 0
      })),
      summary: {
        totals: {
          totalLye: result.total_naoh_weight || result.total_koh_weight || 0,
          totalSuperfat: result.superfat || 0,
          waterLyeRatio: result.water_lye_ratio || 0,
          totalOilWeight: result.total_oil_weight || 0,
          fragranceWeight: result.fragrance_weight || 0,
          lyeConcentration: parseFloat(result.lye_concentration) || 0,
          totalBatchWeight: result.total_batch_weight || 0,
          totalWaterWeight: result.total_water_weight || 0
        },
        breakdowns: result.fatty_acids || {},
        properties: result.properties || {},
        saturations: {
          saturated: parseFloat(result.sat_unsat_ratio?.split('/')[0]) || 0,
          unsaturated: parseFloat(result.sat_unsat_ratio?.split('/')[1]) || 0
        }
      },
      settings: {
        uom: result.uom || 'g',
        ratioKoh: result.ratio_koh || 0,
        soapType: result.soap_type || 'naoh',
        superFat: result.superfat || 5,
        totalUom: result.total_uom || 'g',
        kohPurity: result.koh_purity || 90,
        ratioNaoh: result.ratio_naoh || 0,
        waterRatio: result.water_ratio || 38,
        lyeCalcType: result.lye_calc_type || 'ratio',
        totalWeight: result.total_weight || 0,
        fragrancePpo: result.fragrance_ppo || 0,
        fragranceType: result.fragrance_type || 'ratio',
        waterDiscount: result.water_discount || 0,
        lyeWaterLyeRatio: result.lye_water_lye_ratio || 0,
        lyeWaterWaterRatio: result.lye_water_water_ratio || 0,
        recipeLyeConcentration: result.recipe_lye_concentration || 0
      }
    };

    saveRecipe(recipeData);
  };

  const handleChange = (e, { name, value }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background'
  ];

  return (
    <Form onSubmit={handleSubmit} error={!!error}>
      <Form.Input
        label="Recipe Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        placeholder="Enter a name for your recipe"
        required
      />
      
      <Form.Field>
        <label>Description</label>
        <div className="quill-editor">
          <ReactQuill
            theme="snow"
            value={formData.description}
            onChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
            modules={modules}
            formats={formats}
            style={{ height: '200px', marginBottom: '50px' }}
          />
        </div>
      </Form.Field>

      <Form.Field>
        <label>Notes</label>
        <div className="quill-editor">
          <ReactQuill
            theme="snow"
            value={formData.notes}
            onChange={(content) => setFormData(prev => ({ ...prev, notes: content }))}
            modules={modules}
            formats={formats}
            style={{ height: '200px', marginBottom: '50px' }}
          />
        </div>
      </Form.Field>

      <Form.Field>
        <Checkbox
          label="Make this recipe public"
          name="isPublic"
          checked={formData.isPublic}
          onChange={(_, { name, checked }) => handleChange(null, { name, value: checked })}
        />
      </Form.Field>

      {error && <Message error content={error} />}

      <Button 
        type="submit" 
        primary 
        loading={isLoading}
        disabled={isLoading}
      >
        Save Recipe
      </Button>
    </Form>
  );
}

export default RecipeSaveForm; 